import { ActivationPanel } from "@/components/Activation/ActivationPanel";
import {
  type QuestListItem,
  QuestType,
  useActivationQuests,
} from "@/components/Activation/ActivationPanel/ActivationPanel.utils";
import { CelebrationToast } from "@/components/Onboarding/components/CelebrationToast";
import { DownloadLoginModal } from "@/components/Onboarding/components/DownloadLoginModal";
import type { QuestCompletions } from "@/components/QuestsV2/types";
import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import { ImageBackground } from "@/components/ui/custom/image-background";
import { PrizeWheelV2Modal } from "@/components/ui/custom/prize-wheel/PrizeWheelModal";
import { IMMUTABLE_GAME_ID } from "@/constants";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken, useAccessTokenState } from "@/hooks";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { notifyError } from "@/utils/monitoring";
import { authorizedGet, authorizedPost } from "@/utils/request";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyedMutator } from "swr";
import useSWR from "swr";
import {
  GAMES,
  type Game,
  ONBOARDING_STARTER_QUESTS_COMPLETED_ID,
  ONBOARDING_STARTER_QUESTS_ID,
  OnboardingStep,
} from "../constants";

export type Quest = {
  gameId: string;
  questId: string;
  gems?: number;
  name: string;
  shortName?: string;
  type: QuestType;
  ab?: string;
  externalUrl?: string;
};

const questList: Quest[] = [
  {
    gameId: GAMES.callOfMyth.id,
    questId: GAMES.callOfMyth.quests.wishlist,
    shortName: GAMES.callOfMyth.shortName,
    gems: 20,
    name: GAMES.callOfMyth.name,
    type: QuestType.WISHLIST,
    ab: GAMES.callOfMyth.ab,
  },
  {
    gameId: GAMES.warp.id,
    questId: GAMES.warp.quests.wishlist,
    shortName: GAMES.warp.shortName,
    gems: 10,
    name: GAMES.warp.name,
    type: QuestType.WISHLIST,
    ab: GAMES.warp.ab,
  },
  {
    gameId: GAMES.devilfish.id,
    questId: GAMES.devilfish.quests.wishlist,
    gems: 10,
    name: GAMES.devilfish.name,
    type: QuestType.WISHLIST,
    ab: GAMES.devilfish.ab,
  },
  {
    gameId: GAMES.cartoonCraft.id,
    questId: GAMES.cartoonCraft.quests.wishlist,
    gems: 10,
    name: GAMES.cartoonCraft.name,
    type: QuestType.WISHLIST,
    ab: GAMES.cartoonCraft.ab,
  },
  {
    gameId: GAMES.nomstead.id,
    questId: GAMES.nomstead.quests.downloadInstall,
    gems: 10,
    name: GAMES.nomstead.name,
    type: QuestType.LOGIN_PLAY_GAME,
    ab: GAMES.nomstead.ab,
    externalUrl: GAMES.nomstead.General,
  },
  {
    gameId: GAMES.slimeminer.id,
    questId: GAMES.slimeminer.quests.loginPlayGame,
    gems: 10,
    name: GAMES.slimeminer.name,
    type: QuestType.DOWNLOAD_LOGIN_PLAY,
    ab: GAMES.slimeminer.ab,
  },
  {
    gameId: IMMUTABLE_GAME_ID,
    questId: ONBOARDING_STARTER_QUESTS_ID,
    gems: 100,
    name: "GET YOUR BONUS",
    type: QuestType.BONUS_GEMS,
  },
  {
    gameId: IMMUTABLE_GAME_ID,
    questId: ONBOARDING_STARTER_QUESTS_ID,
    name: "5 Spins of the Prize Wheel",
    type: QuestType.FREE_SPIN,
  },
];

// Celebration messages
const CELEBRATION_MESSAGES = {
  FIRST_QUEST: {
    title: "Great start!",
    description: "You've completed your first quest! Keep going!",
  },
  ALL_COMPLETE: {
    title: "Congratulations!",
    description: "You've completed all quests! Claim your reward now!",
  },
} as const;

const wishlistGame = async (
  gameId: string,
  mutate: KeyedMutator<QuestCompletions>,
  getAccessToken: () => Promise<string | undefined>,
): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) return false;

    await authorizedPost(
      "/v1/games/wishlist",
      { game_id: gameId },
      accessToken,
    );
    await mutate();
    return true;
  } catch (error) {
    notifyError(error, "wishlist");
    return false;
  }
};

export const OnboardingActivation = () => {
  const getAccessToken = useAccessToken();
  const { completedQuests, mutate } = useQuestCompletions(IMMUTABLE_GAME_ID);
  const { activationSpinnerRewardsOct25 } = useFeatureFlags();
  const accessToken = useAccessTokenState();
  const { isLoggedIn } = usePassportProvider();

  const areStarterQuestsCompleted = useMemo(() => {
    return Boolean(completedQuests?.[ONBOARDING_STARTER_QUESTS_ID]);
  }, [completedQuests]);

  const { data: spinsData, mutate: refreshSpins } = useSWR<{
    availableSpins?: number;
  }>(
    accessToken ? ["available-spins", accessToken] : null,
    async () =>
      await authorizedGet<{ availableSpins?: number }>(
        "/v2/rewards/spin/available-spins",
        accessToken as string,
      ),
  );

  const { mutate: refreshPrizes } = useSWR<{
    result?: Array<{
      amount: number;
      name: string;
      type: string;
    }>;
  }>(
    accessToken ? ["prizes", accessToken] : null,
    async () =>
      await authorizedGet<{
        result?: Array<{
          amount: number;
          name: string;
          type: string;
        }>;
      }>("/v2/rewards/spin/prizes-won", accessToken as string),
  );

  // Refresh spins and prizes when starter quests are completed
  useEffect(() => {
    if (areStarterQuestsCompleted) {
      refreshSpins();
      refreshPrizes();
    }
  }, [areStarterQuestsCompleted, refreshSpins, refreshPrizes]);

  const spinsAvailable = spinsData?.availableSpins;

  const { track } = useOnboardingAnalytics();
  const hasViewedRef = useRef(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isPrizeWheelModalOpen, setIsPrizeWheelModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedQuest, setSelectedQuest] = useState<Quest | null>(null);

  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationContent, setCelebrationContent] = useState({
    title: "",
    description: "",
    gems: undefined as number | undefined,
  });
  const hasShownFirstQuestCelebrationRef = useRef(false);

  const handleSpinModalClose = useCallback(
    (open: boolean) => {
      setIsPrizeWheelModalOpen(open);
      if (accessToken && isLoggedIn) {
        refreshSpins();
        refreshPrizes();
      }
    },
    [accessToken, isLoggedIn, refreshSpins, refreshPrizes],
  );

  useEffect(() => {
    if (hasViewedRef.current) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.ONBOARDING_ACTIVATION,
      },
    });
  }, [track]);

  const onListComplete = useCallback(async () => {
    if (areStarterQuestsCompleted) return;

    setLoadingIndex(questList.length - 1);
    try {
      await submitQuestCompletion(
        ONBOARDING_STARTER_QUESTS_COMPLETED_ID,
        getAccessToken,
      );
      await mutate();

      // If we reach here, the submission succeeded
      setCelebrationContent({
        ...CELEBRATION_MESSAGES.ALL_COMPLETE,
        gems: undefined,
      });
      setShowCelebration(true);
    } catch (error) {
      notifyError(error, "questCompletionError");
    } finally {
      setLoadingIndex(null);
    }
  }, [areStarterQuestsCompleted, getAccessToken, mutate]);

  const onBannerClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.ONBOARDING_ACTIVATION,
        control: "activation-banner",
        controlType: ControlType.IMAGE,
      },
    });
  }, [track]);

  const onboardingStarterQuestList: QuestListItem[] = useMemo(() => {
    const isVariant = activationSpinnerRewardsOct25 === "variant";

    return questList
      .filter((quest) => {
        // Filter out quests based on variant
        if (quest.type === QuestType.FREE_SPIN) return isVariant;
        if (quest.type === QuestType.BONUS_GEMS) return !isVariant;
        return true;
      })
      .map((quest, index) => {
        if (quest.type === QuestType.FREE_SPIN) {
          return {
            ...quest,
            onClick: () => {
              handleQuestItemClick(quest, index);
              setIsPrizeWheelModalOpen(true);
            },
          };
        }

        if (quest.type === QuestType.BONUS_GEMS) {
          return quest;
        }

        return {
          ...quest,
          loading: loadingIndex === index,
          onClick: () => handleQuestItemClick(quest, index),
        };
      });
  }, [loadingIndex, activationSpinnerRewardsOct25]);

  const {
    activationQuests,
    completedQuestsCount,
    totalQuestsCount,
    mutate: mutateActivationQuests,
  } = useActivationQuests(onboardingStarterQuestList, onListComplete);

  const handleQuestItemClick = useCallback(
    async (quest: (typeof questList)[number], index: number) => {
      track({
        action: Action.PRESSED,
        extras: {
          id: OnboardingStep.ONBOARDING_ACTIVATION,
          control: `${quest.name}-${quest.type}`
            .replaceAll(" ", "-")
            .toLowerCase(),
          controlType: ControlType.BUTTON,
          gameId: quest.gameId,
          gameName: quest.name,
        },
      });

      if (
        quest.type === QuestType.DOWNLOAD_LOGIN_PLAY ||
        quest.type === QuestType.LOGIN_PLAY_GAME
      ) {
        // Find the game data for the selected quest
        const gameData = Object.values(GAMES).find(
          (game) => game.id === quest.gameId,
        );
        if (gameData) {
          setSelectedGame(gameData);
          setSelectedQuest(quest);
          setIsDownloadModalOpen(true);
        }
      }

      if (quest.type === QuestType.WISHLIST) {
        setLoadingIndex(index);

        // Capture the count BEFORE the API call
        const countBeforeCompletion = completedQuestsCount;

        const success = await wishlistGame(
          quest.gameId,
          mutateActivationQuests,
          getAccessToken,
        );
        setLoadingIndex(null);

        // Show celebration on first quest completion
        const isFirstQuest =
          success &&
          countBeforeCompletion === 0 &&
          !hasShownFirstQuestCelebrationRef.current;

        if (isFirstQuest) {
          hasShownFirstQuestCelebrationRef.current = true;
          setCelebrationContent({
            ...CELEBRATION_MESSAGES.FIRST_QUEST,
            gems: quest.gems,
          });
          setShowCelebration(true);
        }
      }
    },
    [track, getAccessToken, mutateActivationQuests, completedQuestsCount],
  );

  const bannerImage =
    activationSpinnerRewardsOct25 === "variant"
      ? "/images/prize-wheel-activation-banner.webp"
      : "/images/immutable-avatars.webp";

  const title =
    activationSpinnerRewardsOct25 === "variant"
      ? "Welcome Bonus!"
      : "Level up your key!";

  return (
    <>
      <ImageBackground
        staticImageUrl="/images/immutable-avatars.webp"
        backdropClassName="backdrop-blur-sm"
      />

      <CelebrationToast
        open={showCelebration}
        onOpenChange={setShowCelebration}
        title={celebrationContent.title}
        description={celebrationContent.description}
        gems={celebrationContent.gems}
      />

      <ActivationPanel
        banner={bannerImage}
        title={title}
        description="$60k rewards pool closes in:"
        onBannerClick={onBannerClick}
        completedQuestsCount={completedQuestsCount}
        totalQuestsCount={totalQuestsCount}
        activationQuests={activationQuests}
        isCompleted={areStarterQuestsCompleted}
        completedQuests={completedQuests}
        spinsAvailable={spinsAvailable}
      />

      {selectedQuest && selectedGame && (
        <DownloadLoginModal
          quest={selectedQuest}
          open={isDownloadModalOpen}
          onOpenChange={setIsDownloadModalOpen}
          game={selectedGame}
        />
      )}

      <PrizeWheelV2Modal
        open={isPrizeWheelModalOpen}
        onOpenChange={handleSpinModalClose}
        spinsAvailable={spinsAvailable}
      />
    </>
  );
};
